import { parseJwt } from '../utils/jwtDecoder';
import { getItem } from '../utils/authStorage';
import { fetchWithAuthRetry, getAuthHeaders, ensureValidToken } from '../utils/helpers';

/**
 * Fetch Projects list from /api/construction/projects
 * @returns {Promise<Array>}
 */
export async function fetchProjects() {
  const endpoint = '/api/construction/projects';

  try {
    await ensureValidToken();
    const token = getItem('accessToken');
    const payload = token ? parseJwt(token) : null;
    const tenantId = payload?.tenant_id || '';

    const headers = {
      ...getAuthHeaders(),
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
    };

    const formatDate = (val) => {
      if (!val) return null;
      try {
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        }
      } catch {}
      return val;
    };

    const response = await fetchWithAuthRetry(endpoint, { headers });
    if (response.ok) {
      const json = await response.json();
      if (Array.isArray(json)) {
        return json.map((p) => ({
          id: p.code || String(p.id),
          numericId: p.id,
          name: p.name || p.displayName || 'Construction Project',
          code: p.code,
          category: p.category,
          displayName: p.displayName,
          city: p.city,
          state: p.state,
          location: p.city && p.state ? `${p.city}, ${p.state}` : (p.city || p.state || '—'),
          status: p.status || 'Active',
          lastSurvey: formatDate(p.lastSurveyDate) || '—',
          lastUpdated: formatDate(p.lastUpdatedAt) || '—',
          lastUpdatedAt: p.lastUpdatedAt || null,
          lastSurveyDate: p.lastSurveyDate || null,
          imageUrl: p.imageUrl || null,
        }));
      }
    } else {
      console.warn(`GET ${endpoint} → HTTP ${response.status}`);
    }
  } catch (err) {
    console.error('fetchProjects error:', err);
  }

  return [];
}

/**
 * Create a new project via POST /api/construction/projects
 * @param {Object} projectData
 * @returns {Promise<Object>}
 */
export async function createProject(projectData) {
  const endpoint = '/api/construction/projects';

  await ensureValidToken();
  const token = getItem('accessToken');
  const payload = token ? parseJwt(token) : null;
  const tenantId = payload?.tenant_id || '';

  const headers = {
    ...getAuthHeaders(),
    'Content-Type': 'application/json',
    ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
  };

  const response = await fetchWithAuthRetry(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify(projectData),
  });

  if (!response.ok) {
    let errorDetail = `HTTP ${response.status}`;
    try {
      const rawText = await response.text();
      if (rawText) {
        try {
          const errJson = JSON.parse(rawText);
          errorDetail = errJson.error || errJson.message || errJson.detail || rawText;
        } catch {
          errorDetail = rawText;
        }
      }
    } catch {
      // response stream could not be read
    }
    if (response.status === 401 && (!errorDetail || errorDetail === 'HTTP 401')) {
      errorDetail = 'Session expired or unauthorized. Please log in again.';
    }
    throw new Error(errorDetail);
  }

  return await response.json();
}

/**
 * Upload a project image to Command Center media storage (/command-center/media/media/projects/)
 * @param {File} file
 * @returns {Promise<string>} relative URL of uploaded image (e.g. /media/projects/tenant_acme/proj_abc.jpg)
 */
export async function uploadProjectImage(file) {
  const endpoint = '/command-center/media/media/projects/';

  await ensureValidToken();
  const token = getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetchWithAuthRetry(endpoint, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    let errorDetail = `Upload failed with status ${response.status}`;
    try {
      const rawText = await response.text();
      if (rawText) {
        try {
          const errJson = JSON.parse(rawText);
          errorDetail = errJson.error || errJson.message || errJson.detail || rawText;
        } catch {
          errorDetail = rawText;
        }
      }
    } catch {
      // response stream could not be read
    }
    if (response.status === 401 && (!errorDetail || errorDetail.includes('401'))) {
      errorDetail = 'Session expired or unauthorized. Please log in again.';
    }
    throw new Error(errorDetail);
  }

  const result = await response.json();
  return result.url;
}

/**
 * Update an existing project via PATCH /api/construction/projects/{id}
 * @param {number|string} id - Project numeric ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>}
 */
export async function updateProject(id, updates) {
  const endpoint = `/api/construction/projects/${id}`;

  await ensureValidToken();
  const token = getItem('accessToken');
  const payload = token ? parseJwt(token) : null;
  const tenantId = payload?.tenant_id || '';

  const headers = {
    ...getAuthHeaders(),
    'Content-Type': 'application/json',
    ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
  };

  const response = await fetchWithAuthRetry(endpoint, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    let errorDetail = `HTTP ${response.status}`;
    try {
      const rawText = await response.text();
      if (rawText) {
        try {
          const errJson = JSON.parse(rawText);
          errorDetail = errJson.error || errJson.message || errJson.detail || rawText;
        } catch {
          errorDetail = rawText;
        }
      }
    } catch {}
    throw new Error(errorDetail);
  }

  return await response.json();
}



